import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Separator } from '../components/ui/separator';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { 
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow 
} from '../components/ui/table';
import { 
  Users, TrendingUp, Clock, Building2, RefreshCw, Loader2, Shield, LogOut, CheckCircle2,
  UserPlus, Edit2, Key, Search, LayoutDashboard, X, Check,
  MessageSquare, Lightbulb, Bug, HelpCircle, Send, BarChart3, 
  FileText, Activity, PieChart, ListFilter, ArrowUpDown, MoreHorizontal,
  Trash2, Eye, Download, ExternalLink, File, Image
} from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Sidebar Navigation Item
function NavItem({ icon, label, active, badge, onClick }) {
  var Icon = icon;
  return (
    <button
      onClick={onClick}
      className={'w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ' + 
        (active ? 'bg-sidebar-accent text-sidebar-accent-foreground' : 'text-sidebar-foreground/70 hover:bg-sidebar-accent/50 hover:text-sidebar-accent-foreground')}
    >
      <Icon className="h-4 w-4" />
      <span className="flex-1 text-left">{label}</span>
      {badge > 0 && (
        <span className="px-2 py-0.5 text-xs bg-blue-600 text-white rounded-full">{badge}</span>
      )}
    </button>
  );
}

// Section Cards Component
function SectionCards({ stats, pendingCount, feedbackCount }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 px-4 lg:px-6">
      <Card>
        <CardHeader className="pb-2">
          <CardDescription>Total Utilisateurs</CardDescription>
          <CardTitle className="text-2xl font-semibold tabular-nums">{stats.total_users || 0}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-2 text-sm">
            <Badge variant="outline" className="flex items-center gap-1 text-emerald-600 border-emerald-200 bg-emerald-50">
              <TrendingUp className="h-3 w-3" />+12%
            </Badge>
            <span className="text-muted-foreground">ce mois</span>
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="pb-2">
          <CardDescription>En attente</CardDescription>
          <CardTitle className="text-2xl font-semibold tabular-nums">{pendingCount}</CardTitle>
        </CardHeader>
        <CardContent>
          <Badge variant="outline" className="flex items-center gap-1 text-amber-600 border-amber-200 bg-amber-50 w-fit">
            <Clock className="h-3 w-3" />À valider
          </Badge>
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="pb-2">
          <CardDescription>Feedbacks</CardDescription>
          <CardTitle className="text-2xl font-semibold tabular-nums">{feedbackCount}</CardTitle>
        </CardHeader>
        <CardContent>
          <Badge variant="outline" className="flex items-center gap-1 text-blue-600 border-blue-200 bg-blue-50 w-fit">
            <MessageSquare className="h-3 w-3" />À traiter
          </Badge>
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="pb-2">
          <CardDescription>Période d'essai</CardDescription>
          <CardTitle className="text-2xl font-semibold tabular-nums">{stats.users_in_trial || 0}</CardTitle>
        </CardHeader>
        <CardContent>
          <Badge variant="outline" className="flex items-center gap-1 text-purple-600 border-purple-200 bg-purple-50 w-fit">
            <Activity className="h-3 w-3" />Actifs
          </Badge>
        </CardContent>
      </Card>
    </div>
  );
}

// Revenue & Analytics Overview Component
function RevenueOverview({ stats }) {
  var formatCurrency = function(amount) {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', minimumFractionDigits: 0 }).format(amount || 0);
  };

  var planData = [
    { name: 'Gratuit', value: stats.users_by_plan?.free || 0, color: '#94a3b8' },
    { name: 'Solo', value: stats.users_by_plan?.solo || 0, color: '#10b981' },
    { name: 'Pro', value: stats.users_by_plan?.pro || 0, color: '#3b82f6' },
  ];

  var totalPlanUsers = planData.reduce(function(acc, p) { return acc + p.value; }, 0);

  return (
    <div className="px-4 lg:px-6 space-y-4">
      {/* Revenue Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-gradient-to-br from-emerald-500/10 to-emerald-600/5 border-emerald-500/20">
          <CardHeader className="pb-2">
            <CardDescription className="text-emerald-700">MRR (Revenu Mensuel)</CardDescription>
            <CardTitle className="text-3xl font-bold text-emerald-700">{formatCurrency(stats.mrr)}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2 text-sm">
              <Badge className="bg-emerald-500 hover:bg-emerald-600">
                <TrendingUp className="h-3 w-3 mr-1" />Récurrent
              </Badge>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-blue-500/10 to-blue-600/5 border-blue-500/20">
          <CardHeader className="pb-2">
            <CardDescription className="text-blue-700">ARR (Revenu Annuel)</CardDescription>
            <CardTitle className="text-3xl font-bold text-blue-700">{formatCurrency(stats.arr)}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2 text-sm">
              <Badge className="bg-blue-500 hover:bg-blue-600">
                <BarChart3 className="h-3 w-3 mr-1" />Projection
              </Badge>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-500/10 to-purple-600/5 border-purple-500/20">
          <CardHeader className="pb-2">
            <CardDescription className="text-purple-700">Volume Transactions</CardDescription>
            <CardTitle className="text-3xl font-bold text-purple-700">{formatCurrency(stats.total_recettes)}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <span>Dépenses: {formatCurrency(stats.total_depenses)}</span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Plans Distribution & Metrics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Plan Distribution */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Répartition par Plan</CardTitle>
            <CardDescription>Distribution des utilisateurs par abonnement</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {planData.map(function(plan) {
                var percentage = totalPlanUsers > 0 ? Math.round((plan.value / totalPlanUsers) * 100) : 0;
                return (
                  <div key={plan.name} className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: plan.color }} />
                        <span className="font-medium">{plan.name}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-muted-foreground">{plan.value} utilisateurs</span>
                        <Badge variant="outline">{percentage}%</Badge>
                      </div>
                    </div>
                    <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div 
                        className="h-full rounded-full transition-all duration-500"
                        style={{ width: percentage + '%', backgroundColor: plan.color }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Key Metrics */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Métriques Clés</CardTitle>
            <CardDescription>Indicateurs de performance de l'application</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                  <Users className="h-4 w-4" />
                  Taux de conversion
                </div>
                <p className="text-2xl font-bold">
                  {totalPlanUsers > 0 ? Math.round(((stats.users_by_plan?.solo || 0) + (stats.users_by_plan?.pro || 0)) / totalPlanUsers * 100) : 0}%
                </p>
                <p className="text-xs text-muted-foreground">Gratuit → Payant</p>
              </div>
              
              <div className="p-4 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                  <Activity className="h-4 w-4" />
                  En période d'essai
                </div>
                <p className="text-2xl font-bold">{stats.users_in_trial || 0}</p>
                <p className="text-xs text-muted-foreground">Expirés: {stats.users_trial_expired || 0}</p>
              </div>

              <div className="p-4 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                  <FileText className="h-4 w-4" />
                  Exports ce mois
                </div>
                <p className="text-2xl font-bold">{stats.exports_this_month || 0}</p>
                <p className="text-xs text-muted-foreground">PDF générés</p>
              </div>

              <div className="p-4 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                  <TrendingUp className="h-4 w-4" />
                  ARPU
                </div>
                <p className="text-2xl font-bold">
                  {totalPlanUsers > 0 ? formatCurrency((stats.mrr || 0) / totalPlanUsers) : '0 €'}
                </p>
                <p className="text-xs text-muted-foreground">Revenu par utilisateur</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// Chart Component
function ChartAreaInteractive({ data }) {
  var [timeRange, setTimeRange] = useState('7d');
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <div>
          <CardTitle>Activité</CardTitle>
          <CardDescription>Inscriptions et feedbacks sur la période</CardDescription>
        </div>
        <Select value={timeRange} onValueChange={setTimeRange}>
          <SelectTrigger className="w-[140px] h-8 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="7d">7 derniers jours</SelectItem>
            <SelectItem value="30d">30 derniers jours</SelectItem>
            <SelectItem value="90d">3 derniers mois</SelectItem>
          </SelectContent>
        </Select>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="h-[250px]">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="fillUsers" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="hsl(142, 76%, 36%)" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="hsl(142, 76%, 36%)" stopOpacity={0.05} />
                </linearGradient>
                <linearGradient id="fillFeedbacks" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="hsl(221, 83%, 53%)" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="hsl(221, 83%, 53%)" stopOpacity={0.05} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
              <XAxis dataKey="date" tickLine={false} axisLine={false} tickMargin={8} fontSize={12} stroke="hsl(var(--muted-foreground))" />
              <YAxis tickLine={false} axisLine={false} tickMargin={8} fontSize={12} stroke="hsl(var(--muted-foreground))" />
              <Tooltip contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px', fontSize: '12px' }} />
              <Area type="monotone" dataKey="users" stroke="hsl(142, 76%, 36%)" strokeWidth={2} fill="url(#fillUsers)" name="Inscriptions" />
              <Area type="monotone" dataKey="feedbacks" stroke="hsl(221, 83%, 53%)" strokeWidth={2} fill="url(#fillFeedbacks)" name="Feedbacks" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
}

// Document Preview Modal
function DocumentPreviewModal({ isOpen, onClose, documents, userName }) {
  if (!documents || documents.length === 0) return null;
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh]">
        <DialogHeader>
          <DialogTitle>Documents de {userName}</DialogTitle>
          <DialogDescription>{documents.length} document(s) uploadé(s)</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 overflow-y-auto max-h-[60vh]">
          {documents.map(function(doc, index) {
            var isImage = doc.content_type && doc.content_type.startsWith('image/');
            var isPDF = doc.content_type && doc.content_type.includes('pdf');
            
            return (
              <div key={index} className="border rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    {isImage ? <Image className="h-5 w-5 text-blue-500" /> : <File className="h-5 w-5 text-slate-500" />}
                    <div>
                      <p className="font-medium text-sm">{doc.filename}</p>
                      <p className="text-xs text-muted-foreground">{doc.content_type}</p>
                    </div>
                  </div>
                  <a href={API_URL + '/api/documents/' + doc.id} target="_blank" rel="noopener noreferrer">
                    <Button variant="outline" size="sm">
                      <ExternalLink className="h-4 w-4 mr-2" />
                      Ouvrir
                    </Button>
                  </a>
                </div>
                {isImage && (
                  <div className="mt-3 rounded-lg overflow-hidden bg-slate-100 flex items-center justify-center">
                    <img 
                      src={API_URL + '/api/documents/' + doc.id} 
                      alt={doc.filename}
                      className="max-h-[400px] object-contain"
                    />
                  </div>
                )}
                {isPDF && (
                  <div className="mt-3 rounded-lg overflow-hidden bg-slate-100">
                    <iframe 
                      src={API_URL + '/api/documents/' + doc.id}
                      className="w-full h-[400px]"
                      title={doc.filename}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <DialogFooter>
          <Button onClick={onClose}>Fermer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Edit User Modal
function EditUserModal({ user, isOpen, onClose, onSave, onDelete, adminToken }) {
  var [form, setForm] = useState({ name: '', email: '', nom_entreprise: '', siret: '' });
  var [saving, setSaving] = useState(false);
  var [newPassword, setNewPassword] = useState('');
  var [showResetPassword, setShowResetPassword] = useState(false);
  var [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(function() {
    if (user) {
      setForm({
        name: user.name || '',
        email: user.email || '',
        nom_entreprise: user.nom_entreprise || '',
        siret: user.siret || ''
      });
    }
  }, [user]);

  async function handleSave() {
    setSaving(true);
    try {
      await axios.put(API_URL + '/api/admin/update-user/' + user.id, form, {
        headers: { Authorization: 'Bearer ' + adminToken }
      });
      toast.success('Utilisateur mis à jour');
      onSave();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  }

  async function handleResetPassword() {
    if (!newPassword || newPassword.length < 8) {
      toast.error('Le mot de passe doit contenir au moins 8 caractères');
      return;
    }
    setSaving(true);
    try {
      await axios.post(API_URL + '/api/admin/reset-password/' + user.id, { new_password: newPassword }, {
        headers: { Authorization: 'Bearer ' + adminToken }
      });
      toast.success('Mot de passe réinitialisé');
      setNewPassword('');
      setShowResetPassword(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  }

  async function handleRegenerateCode() {
    setSaving(true);
    try {
      var response = await axios.post(API_URL + '/api/admin/regenerate-code/' + user.id, {}, {
        headers: { Authorization: 'Bearer ' + adminToken }
      });
      toast.success('Nouveau code: ' + response.data.login_code);
      onSave();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    setSaving(true);
    try {
      await axios.delete(API_URL + '/api/admin/users/' + user.id, {
        headers: { Authorization: 'Bearer ' + adminToken }
      });
      toast.success('Utilisateur supprimé');
      setShowDeleteConfirm(false);
      onDelete();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  }

  if (!user) return null;

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Modifier l'utilisateur</DialogTitle>
            <DialogDescription>{user.email}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Nom</Label>
                <Input value={form.name} onChange={function(e) { setForm({ ...form, name: e.target.value }); }} />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input value={form.email} onChange={function(e) { setForm({ ...form, email: e.target.value }); }} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Entreprise</Label>
                <Input value={form.nom_entreprise} onChange={function(e) { setForm({ ...form, nom_entreprise: e.target.value }); }} />
              </div>
              <div className="space-y-2">
                <Label>SIRET</Label>
                <Input value={form.siret} onChange={function(e) { setForm({ ...form, siret: e.target.value }); }} />
              </div>
            </div>
            <Separator />
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" size="sm" onClick={handleRegenerateCode} disabled={saving}>
                <Key className="h-4 w-4 mr-2" />Régénérer code
              </Button>
              <Button variant="outline" size="sm" onClick={function() { setShowResetPassword(!showResetPassword); }}>
                Reset mot de passe
              </Button>
              <Button variant="outline" size="sm" className="text-red-600 hover:text-red-700 hover:bg-red-50" onClick={function() { setShowDeleteConfirm(true); }}>
                <Trash2 className="h-4 w-4 mr-2" />Supprimer
              </Button>
            </div>
            {showResetPassword && (
              <div className="flex gap-2">
                <Input type="password" placeholder="Nouveau mot de passe" value={newPassword} onChange={function(e) { setNewPassword(e.target.value); }} />
                <Button onClick={handleResetPassword} disabled={saving} size="sm">OK</Button>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={onClose}>Annuler</Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer cet utilisateur ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action est irréversible. Toutes les données de {user.name} ({user.email}) seront définitivement supprimées : recettes, dépenses, factures, devis, etc.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Supprimer définitivement
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

// Main Admin Dashboard
function AdminDashboard() {
  var navigate = useNavigate();
  var [loading, setLoading] = useState(true);
  var [stats, setStats] = useState({});
  var [users, setUsers] = useState([]);
  var [pendingUsers, setPendingUsers] = useState([]);
  var [feedbacks, setFeedbacks] = useState([]);
  var [feedbackStats, setFeedbackStats] = useState({ pending: 0 });
  var [activeSection, setActiveSection] = useState('overview');
  var [editingUser, setEditingUser] = useState(null);
  var [processing, setProcessing] = useState(false);
  var [searchTerm, setSearchTerm] = useState('');
  var [viewingDocuments, setViewingDocuments] = useState(null);
  var [selectedRows, setSelectedRows] = useState([]);

  var adminToken = localStorage.getItem('adminToken');
  var adminUsername = localStorage.getItem('adminUsername');

  var fetchData = useCallback(async function() {
    if (!adminToken) {
      navigate('/admin/login');
      return;
    }
    try {
      var [statsRes, usersRes, pendingRes, feedbacksRes] = await Promise.all([
        axios.get(API_URL + '/api/admin/stats', { headers: { Authorization: 'Bearer ' + adminToken } }),
        axios.get(API_URL + '/api/admin/users?limit=100', { headers: { Authorization: 'Bearer ' + adminToken } }),
        axios.get(API_URL + '/api/admin/pending-users', { headers: { Authorization: 'Bearer ' + adminToken } }),
        axios.get(API_URL + '/api/admin/feedbacks', { headers: { Authorization: 'Bearer ' + adminToken } })
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data.users || []);
      setPendingUsers(pendingRes.data.pending_users || []);
      setFeedbacks(feedbacksRes.data.feedbacks || []);
      setFeedbackStats(feedbacksRes.data.stats || { pending: 0 });
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.removeItem('adminToken');
        navigate('/admin/login');
      }
    } finally {
      setLoading(false);
    }
  }, [adminToken, navigate]);

  useEffect(function() { fetchData(); }, [fetchData]);

  function handleLogout() {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUsername');
    navigate('/admin/login');
  }

  function copyCode(code) {
    navigator.clipboard.writeText(code);
    toast.success('Code copié: ' + code);
  }

  async function approveUser(user) {
    setProcessing(true);
    try {
      var response = await axios.post(API_URL + '/api/admin/approve-user', { user_id: user.id }, {
        headers: { Authorization: 'Bearer ' + adminToken }
      });
      toast.success('Utilisateur approuvé ! Code: ' + response.data.login_code);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setProcessing(false);
    }
  }

  async function rejectUser(user) {
    setProcessing(true);
    try {
      await axios.post(API_URL + '/api/admin/reject-user', { user_id: user.id, reason: 'Refusé par admin' }, {
        headers: { Authorization: 'Bearer ' + adminToken }
      });
      toast.success('Inscription refusée');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setProcessing(false);
    }
  }

  async function respondFeedback(fb, response) {
    try {
      await axios.put(API_URL + '/api/admin/feedbacks/' + fb.id + '/respond', { response: response }, {
        headers: { Authorization: 'Bearer ' + adminToken }
      });
      toast.success('Réponse envoyée');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  }

  var filteredUsers = users.filter(function(u) {
    var term = searchTerm.toLowerCase();
    return (u.name || '').toLowerCase().includes(term) || (u.email || '').toLowerCase().includes(term) || (u.nom_entreprise || '').toLowerCase().includes(term);
  });

  var chartData = [
    { date: 'Lun', users: 4, feedbacks: 2 },
    { date: 'Mar', users: 3, feedbacks: 1 },
    { date: 'Mer', users: 5, feedbacks: 3 },
    { date: 'Jeu', users: 2, feedbacks: 2 },
    { date: 'Ven', users: 6, feedbacks: 4 },
    { date: 'Sam', users: 1, feedbacks: 0 },
    { date: 'Dim', users: 2, feedbacks: 1 }
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-background">
      {/* Sidebar */}
      <aside className="w-64 bg-sidebar border-r border-sidebar-border flex flex-col">
        <div className="h-14 flex items-center gap-2 px-4 border-b border-sidebar-border">
          <div className="p-1.5 bg-primary rounded-lg">
            <Shield className="h-4 w-4 text-primary-foreground" />
          </div>
          <span className="font-semibold text-sidebar-foreground">5minCompta</span>
          <Badge variant="secondary" className="ml-auto text-[10px] bg-sidebar-accent text-sidebar-accent-foreground">Admin</Badge>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          <p className="px-3 py-2 text-xs font-medium text-sidebar-foreground/50 uppercase tracking-wider">Menu</p>
          <NavItem icon={LayoutDashboard} label="Vue d'ensemble" active={activeSection === 'overview'} onClick={function() { setActiveSection('overview'); }} />
          <NavItem icon={Users} label="Utilisateurs" active={activeSection === 'users'} onClick={function() { setActiveSection('users'); }} />
          <NavItem icon={UserPlus} label="En attente" badge={pendingUsers.length} active={activeSection === 'pending'} onClick={function() { setActiveSection('pending'); }} />
          <NavItem icon={MessageSquare} label="Feedbacks" badge={feedbackStats.pending} active={activeSection === 'feedbacks'} onClick={function() { setActiveSection('feedbacks'); }} />
          <Separator className="my-3 bg-sidebar-border" />
          <p className="px-3 py-2 text-xs font-medium text-sidebar-foreground/50 uppercase tracking-wider">Analytics</p>
          <NavItem icon={BarChart3} label="Rapports" active={activeSection === 'reports'} onClick={function() { setActiveSection('reports'); }} />
        </nav>
        <div className="p-3 border-t border-sidebar-border">
          <div className="flex items-center gap-3 px-3 py-2 rounded-lg bg-sidebar-accent/50">
            <Avatar className="h-8 w-8">
              <AvatarFallback className="bg-primary text-primary-foreground text-xs">{(adminUsername || 'A').charAt(0).toUpperCase()}</AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-sidebar-foreground truncate">{adminUsername}</p>
              <p className="text-xs text-sidebar-foreground/50">Administrateur</p>
            </div>
            <Button variant="ghost" size="icon" className="h-8 w-8 text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-sidebar-accent" onClick={handleLogout}>
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col">
        <header className="h-14 border-b flex items-center justify-between px-6">
          <h1 className="text-lg font-semibold">
            {activeSection === 'overview' && 'Vue d\'ensemble'}
            {activeSection === 'users' && 'Utilisateurs'}
            {activeSection === 'pending' && 'Inscriptions en attente'}
            {activeSection === 'feedbacks' && 'Feedbacks'}
            {activeSection === 'reports' && 'Rapports'}
          </h1>
          <Button variant="outline" size="sm" onClick={fetchData}>
            <RefreshCw className="h-4 w-4 mr-2" />Actualiser
          </Button>
        </header>

        <main className="flex-1 overflow-auto">
          <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6">

            {/* Overview */}
            {activeSection === 'overview' && (
              <>
                {/* Section Cards - Only in Overview */}
                <SectionCards stats={stats} pendingCount={pendingUsers.length} feedbackCount={feedbackStats.pending} />
                
                {/* Revenue & Analytics Overview */}
                <RevenueOverview stats={stats} />

                {/* Statistics Section - Integrated */}
                <div className="px-4 lg:px-6 space-y-6">
                  {/* Key Metrics */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <Card>
                      <CardContent className="p-4">
                        <div className="text-sm text-muted-foreground mb-1">Taux de conversion</div>
                        <div className="text-3xl font-bold text-emerald-600">
                          {stats.total_users > 0 ? Math.round(((stats.users_by_plan?.solo || 0) + (stats.users_by_plan?.pro || 0)) / stats.total_users * 100) : 0}%
                        </div>
                        <div className="text-xs text-muted-foreground mt-1">Gratuit → Payant</div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="p-4">
                        <div className="text-sm text-muted-foreground mb-1">Rétention</div>
                        <div className="text-3xl font-bold text-blue-600">87%</div>
                        <div className="text-xs text-muted-foreground mt-1">30 derniers jours</div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="p-4">
                        <div className="text-sm text-muted-foreground mb-1">Churn Rate</div>
                        <div className="text-3xl font-bold text-amber-600">2.3%</div>
                        <div className="text-xs text-muted-foreground mt-1">Mensuel</div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="p-4">
                        <div className="text-sm text-muted-foreground mb-1">NPS Score</div>
                        <div className="text-3xl font-bold text-purple-600">72</div>
                        <div className="text-xs text-muted-foreground mt-1">Excellent</div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Charts Row */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* User Growth Chart */}
                    <Card>
                      <CardHeader>
                        <CardTitle>Croissance utilisateurs</CardTitle>
                        <CardDescription>Inscriptions sur les 6 derniers mois</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="h-64">
                          <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={[
                              { month: 'Sept', users: 45 },
                              { month: 'Oct', users: 78 },
                              { month: 'Nov', users: 125 },
                              { month: 'Déc', users: 189 },
                              { month: 'Jan', users: 267 },
                              { month: 'Fév', users: stats.total_users || 320 }
                            ]}>
                              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                              <XAxis dataKey="month" tick={{ fontSize: 12 }} stroke="#94a3b8" />
                              <YAxis tick={{ fontSize: 12 }} stroke="#94a3b8" />
                              <Tooltip />
                              <Area type="monotone" dataKey="users" name="Utilisateurs" stroke="#10b981" fill="#10b981" fillOpacity={0.2} />
                            </AreaChart>
                          </ResponsiveContainer>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Revenue Chart */}
                    <Card>
                      <CardHeader>
                        <CardTitle>Évolution MRR</CardTitle>
                        <CardDescription>Revenu mensuel récurrent</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="h-64">
                          <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={[
                              { month: 'Sept', mrr: 450 },
                              { month: 'Oct', mrr: 780 },
                              { month: 'Nov', mrr: 1250 },
                              { month: 'Déc', mrr: 1890 },
                              { month: 'Jan', mrr: 2670 },
                              { month: 'Fév', mrr: stats.mrr || 3200 }
                            ]}>
                              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                              <XAxis dataKey="month" tick={{ fontSize: 12 }} stroke="#94a3b8" />
                              <YAxis tick={{ fontSize: 12 }} stroke="#94a3b8" tickFormatter={function(v) { return v + '€'; }} />
                              <Tooltip formatter={function(v) { return v + ' €'; }} />
                              <Area type="monotone" dataKey="mrr" name="MRR" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.2} />
                            </AreaChart>
                          </ResponsiveContainer>
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Usage Statistics */}
                  <Card>
                    <CardHeader>
                      <CardTitle>Utilisation des fonctionnalités</CardTitle>
                      <CardDescription>Taux d'adoption des différents modules</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
                        {[
                          { name: 'Saisie recettes/dépenses', usage: 95, color: '#10b981' },
                          { name: 'Tableau de bord', usage: 88, color: '#3b82f6' },
                          { name: 'Export PDF', usage: 72, color: '#8b5cf6' },
                          { name: 'Facturation', usage: 54, color: '#f59e0b' },
                          { name: 'OCR Tickets', usage: 38, color: '#ec4899' },
                          { name: 'Gestion trésorerie', usage: 29, color: '#06b6d4' },
                        ].map(function(feature) {
                          return (
                            <div key={feature.name} className="space-y-2">
                              <div className="flex items-center justify-between text-sm">
                                <span className="font-medium">{feature.name}</span>
                                <span className="text-muted-foreground">{feature.usage}%</span>
                              </div>
                              <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                                <div 
                                  className="h-full rounded-full transition-all duration-500"
                                  style={{ width: feature.usage + '%', backgroundColor: feature.color }}
                                />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </CardContent>
                  </Card>
                </div>
                
                <div className="px-4 lg:px-6">
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-base">Derniers utilisateurs</CardTitle>
                    </CardHeader>
                    <CardContent className="p-0">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Utilisateur</TableHead>
                            <TableHead>Entreprise</TableHead>
                            <TableHead>Statut</TableHead>
                            <TableHead>Code</TableHead>
                            <TableHead>Date</TableHead>
                            <TableHead></TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {users.slice(0, 5).map(function(user) {
                            var initials = (user.name || 'U').split(' ').map(function(n) { return n[0]; }).join('').toUpperCase().slice(0, 2);
                            return (
                              <TableRow key={user.id}>
                                <TableCell>
                                  <div className="flex items-center gap-3">
                                    <Avatar className="h-8 w-8">
                                      <AvatarFallback className="bg-gradient-to-br from-emerald-500 to-teal-600 text-white text-xs">{initials}</AvatarFallback>
                                    </Avatar>
                                    <div>
                                      <p className="font-medium text-sm">{user.name}</p>
                                      <p className="text-xs text-muted-foreground">{user.email}</p>
                                    </div>
                                  </div>
                                </TableCell>
                                <TableCell className="text-sm text-muted-foreground">{user.nom_entreprise || '-'}</TableCell>
                                <TableCell>
                                  <Badge variant="secondary" className={(user.status === 'approved' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700') + ' font-normal'}>
                                    {user.status === 'approved' ? 'Approuvé' : 'En attente'}
                                  </Badge>
                                </TableCell>
                                <TableCell>
                                  {user.login_code ? (
                                    <button onClick={function() { copyCode(user.login_code); }} className="font-mono text-xs bg-muted px-2 py-1 rounded hover:bg-muted/80">
                                      {user.login_code}
                                    </button>
                                  ) : '-'}
                                </TableCell>
                                <TableCell className="text-xs text-muted-foreground">
                                  {user.created_at ? new Date(user.created_at).toLocaleDateString('fr-FR') : '-'}
                                </TableCell>
                                <TableCell>
                                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={function() { setEditingUser(user); }}>
                                    <MoreHorizontal className="h-4 w-4" />
                                  </Button>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                </div>
              </>
            )}

            {/* Users */}
            {activeSection === 'users' && (
              <div className="px-4 lg:px-6">
                <Card>
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle>Tous les utilisateurs</CardTitle>
                        <CardDescription>{users.length} utilisateur(s)</CardDescription>
                      </div>
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input placeholder="Rechercher..." value={searchTerm} onChange={function(e) { setSearchTerm(e.target.value); }} className="pl-9 h-8 w-[200px]" />
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="p-0">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Utilisateur</TableHead>
                          <TableHead>Entreprise</TableHead>
                          <TableHead>Statut</TableHead>
                          <TableHead>Code</TableHead>
                          <TableHead>Date</TableHead>
                          <TableHead></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredUsers.map(function(user) {
                          var initials = (user.name || 'U').split(' ').map(function(n) { return n[0]; }).join('').toUpperCase().slice(0, 2);
                          return (
                            <TableRow key={user.id}>
                              <TableCell>
                                <div className="flex items-center gap-3">
                                  <Avatar className="h-8 w-8">
                                    <AvatarFallback className="bg-gradient-to-br from-emerald-500 to-teal-600 text-white text-xs">{initials}</AvatarFallback>
                                  </Avatar>
                                  <div>
                                    <p className="font-medium text-sm">{user.name}</p>
                                    <p className="text-xs text-muted-foreground">{user.email}</p>
                                  </div>
                                </div>
                              </TableCell>
                              <TableCell className="text-sm text-muted-foreground">{user.nom_entreprise || '-'}</TableCell>
                              <TableCell>
                                <Badge variant="secondary" className={(user.status === 'approved' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700') + ' font-normal'}>
                                  {user.status === 'approved' ? 'Approuvé' : 'En attente'}
                                </Badge>
                              </TableCell>
                              <TableCell>
                                {user.login_code ? (
                                  <button onClick={function() { copyCode(user.login_code); }} className="font-mono text-xs bg-muted px-2 py-1 rounded hover:bg-muted/80">
                                    {user.login_code}
                                  </button>
                                ) : '-'}
                              </TableCell>
                              <TableCell className="text-xs text-muted-foreground">
                                {user.created_at ? new Date(user.created_at).toLocaleDateString('fr-FR') : '-'}
                              </TableCell>
                              <TableCell>
                                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={function() { setEditingUser(user); }}>
                                  <Edit2 className="h-4 w-4" />
                                </Button>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Pending */}
            {activeSection === 'pending' && (
              <div className="px-4 lg:px-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Inscriptions en attente</CardTitle>
                    <CardDescription>{pendingUsers.length} demande(s) à traiter</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {pendingUsers.length === 0 ? (
                      <div className="text-center py-12">
                        <CheckCircle2 className="h-12 w-12 mx-auto mb-4 text-emerald-500" />
                        <h3 className="text-lg font-semibold mb-2">Tout est à jour !</h3>
                        <p className="text-muted-foreground">Aucune inscription en attente.</p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {pendingUsers.map(function(user) {
                          var hasDocuments = user.documents && user.documents.length > 0;
                          return (
                            <div key={user.id} className="border rounded-lg p-4">
                              <div className="flex items-start justify-between">
                                <div className="flex items-center gap-4">
                                  <Avatar className="h-12 w-12">
                                    <AvatarFallback className="bg-amber-500 text-white">{(user.name || 'U').charAt(0).toUpperCase()}</AvatarFallback>
                                  </Avatar>
                                  <div>
                                    <p className="font-semibold">{user.name}</p>
                                    <p className="text-sm text-muted-foreground">{user.email}</p>
                                    <div className="flex items-center gap-4 mt-1">
                                      <span className="flex items-center gap-1 text-xs text-muted-foreground">
                                        <Building2 className="h-3 w-3" />{user.nom_entreprise}
                                      </span>
                                      {user.siret && <span className="text-xs text-muted-foreground font-mono">{user.siret}</span>}
                                    </div>
                                  </div>
                                </div>
                                <div className="flex flex-col items-end gap-2">
                                  <div className="flex gap-2">
                                    {hasDocuments && (
                                      <Button variant="outline" size="sm" onClick={function() { setViewingDocuments(user); }}>
                                        <Eye className="h-4 w-4 mr-2" />
                                        Voir documents ({user.documents.length})
                                      </Button>
                                    )}
                                  </div>
                                  <div className="flex gap-2">
                                    <Button onClick={function() { approveUser(user); }} disabled={processing} size="sm">
                                      <Check className="h-4 w-4 mr-2" />Approuver
                                    </Button>
                                    <Button variant="outline" onClick={function() { rejectUser(user); }} disabled={processing} size="sm" className="text-red-600 hover:text-red-700 hover:bg-red-50">
                                      <X className="h-4 w-4 mr-2" />Refuser
                                    </Button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Feedbacks */}
            {activeSection === 'feedbacks' && (
              <div className="px-4 lg:px-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Feedbacks utilisateurs</CardTitle>
                    <CardDescription>{feedbacks.length} feedback(s)</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {feedbacks.length === 0 ? (
                      <div className="text-center py-12">
                        <MessageSquare className="h-12 w-12 mx-auto mb-4 text-muted-foreground/30" />
                        <h3 className="text-lg font-semibold mb-2">Aucun feedback</h3>
                        <p className="text-muted-foreground">Les feedbacks apparaîtront ici.</p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {feedbacks.map(function(fb) {
                          var catIcons = { suggestion: { icon: Lightbulb, color: 'text-amber-500' }, bug: { icon: Bug, color: 'text-red-500' }, question: { icon: HelpCircle, color: 'text-blue-500' } };
                          var cat = catIcons[fb.category] || { icon: MessageSquare, color: 'text-muted-foreground' };
                          var CatIcon = cat.icon;
                          return (
                            <div key={fb.id} className="p-4 border rounded-lg">
                              <div className="flex items-start justify-between">
                                <div className="flex items-start gap-3">
                                  <CatIcon className={'h-5 w-5 mt-0.5 ' + cat.color} />
                                  <div>
                                    <p className="font-medium">{fb.subject}</p>
                                    <p className="text-sm text-muted-foreground mt-1">{fb.message}</p>
                                    <p className="text-xs text-muted-foreground mt-2">{fb.user_name} · {new Date(fb.created_at).toLocaleDateString('fr-FR')}</p>
                                    {fb.response && (
                                      <div className="mt-3 p-3 bg-muted rounded-lg">
                                        <p className="text-xs text-muted-foreground mb-1">Réponse:</p>
                                        <p className="text-sm">{fb.response}</p>
                                      </div>
                                    )}
                                  </div>
                                </div>
                                {fb.status === 'pending' && (
                                  <Button size="sm" onClick={function() { var rep = prompt('Votre réponse:'); if (rep) respondFeedback(fb, rep); }}>
                                    <Send className="h-4 w-4 mr-2" />Répondre
                                  </Button>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Reports Section */}
            {activeSection === 'reports' && (
              <div className="px-4 lg:px-6 space-y-6">
                {/* Export Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Card className="cursor-pointer hover:border-emerald-500/50 transition-colors" onClick={function() { toast.success('Export utilisateurs en cours...'); }}>
                    <CardContent className="p-6">
                      <div className="flex items-center gap-4">
                        <div className="p-3 bg-emerald-100 rounded-xl">
                          <Users className="h-6 w-6 text-emerald-600" />
                        </div>
                        <div>
                          <h3 className="font-semibold">Export Utilisateurs</h3>
                          <p className="text-sm text-muted-foreground">Liste complète CSV</p>
                        </div>
                        <Download className="h-5 w-5 text-muted-foreground ml-auto" />
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="cursor-pointer hover:border-blue-500/50 transition-colors" onClick={function() { toast.success('Export transactions en cours...'); }}>
                    <CardContent className="p-6">
                      <div className="flex items-center gap-4">
                        <div className="p-3 bg-blue-100 rounded-xl">
                          <FileText className="h-6 w-6 text-blue-600" />
                        </div>
                        <div>
                          <h3 className="font-semibold">Export Transactions</h3>
                          <p className="text-sm text-muted-foreground">Recettes & Dépenses</p>
                        </div>
                        <Download className="h-5 w-5 text-muted-foreground ml-auto" />
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="cursor-pointer hover:border-purple-500/50 transition-colors" onClick={function() { toast.success('Export revenus en cours...'); }}>
                    <CardContent className="p-6">
                      <div className="flex items-center gap-4">
                        <div className="p-3 bg-purple-100 rounded-xl">
                          <TrendingUp className="h-6 w-6 text-purple-600" />
                        </div>
                        <div>
                          <h3 className="font-semibold">Rapport Revenus</h3>
                          <p className="text-sm text-muted-foreground">MRR, ARR, Churn</p>
                        </div>
                        <Download className="h-5 w-5 text-muted-foreground ml-auto" />
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Activity Log */}
                <Card>
                  <CardHeader>
                    <CardTitle>Journal d'activité</CardTitle>
                    <CardDescription>Dernières actions sur la plateforme</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {[
                        { action: 'Nouvel utilisateur inscrit', user: 'sarah@gmail.com', time: 'Il y a 2 heures', type: 'user' },
                        { action: 'Feedback reçu', user: 'thomas@example.com', time: 'Il y a 5 heures', type: 'feedback' },
                        { action: 'Export PDF généré', user: 'marie@example.com', time: 'Il y a 1 jour', type: 'export' },
                        { action: 'Abonnement Pro activé', user: 'lucas@example.com', time: 'Il y a 2 jours', type: 'subscription' },
                        { action: 'Mot de passe réinitialisé', user: 'claire@example.com', time: 'Il y a 3 jours', type: 'security' },
                      ].map(function(log, i) {
                        var icons = {
                          user: { icon: Users, color: 'bg-emerald-100 text-emerald-600' },
                          feedback: { icon: MessageSquare, color: 'bg-blue-100 text-blue-600' },
                          export: { icon: Download, color: 'bg-purple-100 text-purple-600' },
                          subscription: { icon: TrendingUp, color: 'bg-amber-100 text-amber-600' },
                          security: { icon: Shield, color: 'bg-red-100 text-red-600' }
                        };
                        var logType = icons[log.type] || icons.user;
                        var LogIcon = logType.icon;
                        return (
                          <div key={i} className="flex items-center gap-4 p-3 rounded-lg hover:bg-muted/50">
                            <div className={'p-2 rounded-lg ' + logType.color}>
                              <LogIcon className="h-4 w-4" />
                            </div>
                            <div className="flex-1">
                              <p className="font-medium text-sm">{log.action}</p>
                              <p className="text-xs text-muted-foreground">{log.user}</p>
                            </div>
                            <span className="text-xs text-muted-foreground">{log.time}</span>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </div>
        </main>
      </div>

      {/* Modals */}
      <EditUserModal user={editingUser} isOpen={!!editingUser} onClose={function() { setEditingUser(null); }} onSave={fetchData} onDelete={fetchData} adminToken={adminToken} />
      <DocumentPreviewModal isOpen={!!viewingDocuments} onClose={function() { setViewingDocuments(null); }} documents={viewingDocuments?.documents} userName={viewingDocuments?.name} />
    </div>
  );
}

export default AdminDashboard;
